/* eslint-disable max-lines */
import type React from "react";
import { useState, useRef, useEffect } from "react";

import { colorsSelector } from "@redux/colorPalette/selectors";
import { reorderWaitingList, useLastSongSelector, useSetLastSong } from "@redux/player";
import { useWaitingListSelector } from "@redux/player";
import { sessionEndTimestampSelector } from "@redux/session/selectors";
import useSelector from "@redux/useSelector";
import { withBamLayoutSelector } from "@redux/user/selectors";

import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
	type DragStartEvent,
	TouchSensor,
} from "@dnd-kit/core";
import { useDispatch } from "react-redux";
import { useBoolean } from "react-use";
import type { Song } from "src/@types/api-types";

import ScrollButton from "components/Layout/ScrollButton";
import ConditionalWrapper from "components/Layout/StrategyUtils/ConditionalWrapper";
import { getSonglistDuration } from "services/helpers/formatTime";

import {
	StyledWaitingList,
	SectionContent,
	ScrollableContainer,
} from "./WaitingList.style";
import DeleteModal from "./components/DeleteModal";
import EmptyListView from "./components/EmptyListView";
import SongList from "./components/SongList";
import WaitinListHeader from "./components/WaitingListHeader";
import { RemainingTimeBloc } from "./components/RemainingTime/RemainingTimeBloc";
import { DROPPABLETYPES } from "types/droppables";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import DragSongOverlay from "./components/DragSongOverlay/DragSongOverlay";
import { isTouchScreen } from "utils/detectTouchScreen";

interface Props {
	className?: string;
}

const WaitingList: React.FC<Props> = ({ className }) => {
	const refWaitingList = useRef<HTMLDivElement>(null);
	const refSectionContent = useRef<HTMLDivElement>(null);
	const waitingList = useWaitingListSelector();
	const scrollRef = useRef<any>(null);
	const scrollPosition = useRef(0);
	const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
	const [isModalOpen, setIsModalOpen] = useBoolean(false);
	const withBamLayout = useSelector(withBamLayoutSelector);
	const colors = useSelector(colorsSelector);
	const { setLastSong } = useSetLastSong();
	const lastSong = useLastSongSelector();
	const [activeDragItem, setActiveDragItem] = useState<Song | null>(null);

	const detectSensor = isTouchScreen ? TouchSensor : PointerSensor;
	const sensors = useSensors(useSensor(detectSensor));

	const draggableChildElement = ".MuiListItemSecondaryAction-root";

	const dispatch = useDispatch();
	const bottomRef = useRef<HTMLDivElement | null>(null);
	const topRef = useRef<HTMLDivElement | null>(null);
	const sessionEndTimeStamp = useSelector(sessionEndTimestampSelector);

	const handleDragEnd = (event: DragEndEvent) => {
		setActiveDragItem(null);
		const { active, over } = event;

		if (!over || active.id === over.id) return;

		const sourceIndex = Number(active.id);

		if (over.id === DROPPABLETYPES.LAST_SONG) {
			const song = waitingList[sourceIndex];
			if (song) {
				setLastSong(song);
			}
			return;
		}

		const destinationIndex = Number(over.id);

		dispatch(
			reorderWaitingList({
				songStartIndex: sourceIndex,
				songEndIndex: destinationIndex,
			}),
		);
	};

	const handleDragStart = (event: DragStartEvent) => {
		setActiveDragItem(waitingList[Number(event.active.id)]);
		if (scrollRef.current) {
			scrollPosition.current = scrollRef.current.scrollTop;
		}
	};

	const scrollDown = () => {
		bottomRef?.current?.scrollIntoView({ behavior: "smooth" });
	};

	const scrollUp = () => {
		topRef?.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = scrollPosition.current;
		}
		if (
			!refWaitingList?.current?.clientHeight ||
			!refSectionContent?.current?.clientHeight
		) {
			return;
		}

		const areScrollButtonsVisible =
			refSectionContent?.current?.clientHeight >
			refWaitingList?.current?.clientHeight;
		setShowScrollButton(areScrollButtonsVisible);
	}, [waitingList]);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
			modifiers={[restrictToVerticalAxis]}
			onDragStart={handleDragStart}
		>
			<StyledWaitingList {...{ className, colors }} ref={refWaitingList}>
				<WaitinListHeader
					isTrashHidden={!!waitingList.length}
					waitingListDuration={getSonglistDuration(waitingList, lastSong.song?.length || 0)}
					{...{ setIsModalOpen }}
				/>
				<ConditionalWrapper
					condition={withBamLayout}
					wrapper={(children) => (
						<ScrollableContainer
							// ref={scrollRef}
							vertical
							ignoreElements={draggableChildElement}
							nativeMobileScroll={false}
						>
							{children}
						</ScrollableContainer>
					)}
				>
					<SectionContent>
						{waitingList.length || sessionEndTimeStamp ? (
							<ConditionalWrapper
								wrapper={(children) => (
									<ScrollButton {...{ scrollDown, scrollUp }}>
										{children}
									</ScrollButton>
								)}
								condition={showScrollButton}
							>
								<div ref={refSectionContent}>
									<div ref={topRef} />
									<SongList />
									<div ref={bottomRef} />
								</div>
							</ConditionalWrapper>
						) : (
							<EmptyListView />
						)}
					</SectionContent>
				</ConditionalWrapper>
				{sessionEndTimeStamp && <RemainingTimeBloc />}
			</StyledWaitingList>
			<DeleteModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
			<DragSongOverlay
				colors={colors}
				song={activeDragItem}
				activeDragItem={!!activeDragItem}
			/>
		</DndContext>
	);
};

export default WaitingList;
